<template>
  <section class="px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <header class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex min-w-0 items-start gap-3">
            <HistoryBackButton />
            <div class="min-w-0">
              <p class="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Business reviews</p>
              <h1 class="mt-1 truncate text-2xl font-semibold text-slate-950 sm:text-3xl">{{ pageTitle }}</h1>
              <p class="mt-1 text-sm text-slate-500">Reviews and customer photos for this business.</p>
            </div>
          </div>

          <span class="inline-flex w-fit items-center gap-2 rounded-full bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700">
            <i class="ri-chat-quote-line" aria-hidden="true"></i>
            <span>{{ reviews.length }} review{{ reviews.length === 1 ? '' : 's' }}</span>
          </span>
        </div>
      </header>

      <div class="mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div class="flex items-center justify-between gap-3 border-b border-slate-200 pb-4">
          <div>
            <p class="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Related reviews</p>
            <h2 class="mt-1 text-xl font-semibold text-slate-950">What people are saying</h2>
          </div>
          <button
            class="inline-flex min-h-9 items-center gap-2 rounded-full bg-slate-100 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
            type="button"
            :disabled="isLoading"
            @click="loadReviews"
          >
            <i class="ri-refresh-line" :class="{ 'animate-spin': isLoading }" aria-hidden="true"></i>
            <span>Refresh</span>
          </button>
        </div>

        <div v-if="isLoading" class="business-reviews-grid mt-5">
          <div
            v-for="(column, columnIndex) in skeletonColumns"
            :key="`review-skeleton-column-${columnIndex}`"
            class="business-reviews-column"
          >
            <FlyerCardSkeleton v-for="index in column" :key="`review-skeleton-${index}`" :index="index" />
          </div>
        </div>

        <div v-else-if="loadError || !reviews.length" class="py-12 text-center">
          <i class="ri-chat-quote-line text-4xl text-slate-300" aria-hidden="true"></i>
          <p class="mt-3 text-lg font-semibold text-slate-900">{{ loadError ? 'Unable to load reviews' : 'No reviews found' }}</p>
          <p class="mt-2 text-sm text-slate-500">{{ loadError || 'This business does not have any reviews yet.' }}</p>
        </div>

        <div v-else class="business-reviews-grid mt-5">
          <div
            v-for="(column, columnIndex) in reviewColumns"
            :key="`review-column-${columnIndex}`"
            class="business-reviews-column"
          >
            <FlyerCard
              v-for="review in column"
              :key="review.id"
              :flyer="review"
              view="masonry"
              review-content-only
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import FlyerCard from '@/components/common/FlyerCard.vue'
import FlyerCardSkeleton from '@/components/common/FlyerCardSkeleton.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import { firstValue, normalizeFlyerDetail } from '@/utils/flyerDetail'

const route = useRoute()
const session = useSessionStore()
const reviews = ref([])
const isLoading = ref(false)
const loadError = ref('')
const columnCount = ref(1)

const businessId = computed(() => String(route.params.businessId || '').trim())
const requestedBusinessName = computed(() => String(route.query.name || '').trim())
const pageTitle = computed(() => requestedBusinessName.value || reviews.value[0]?.business_name || 'Business reviews')

const distributeByColumn = (items, count) => {
  const columns = Array.from({ length: Math.max(1, count) }, () => [])
  items.forEach((item, index) => columns[index % columns.length].push(item))
  return columns
}

const reviewColumns = computed(() => distributeByColumn(reviews.value, columnCount.value))
const skeletonColumns = computed(() => distributeByColumn(Array.from({ length: 8 }, (_, index) => index + 1), columnCount.value))

const updateColumnCount = () => {
  if (typeof window === 'undefined') return
  columnCount.value = window.innerWidth >= 1280 ? 4 : window.innerWidth >= 768 ? 2 : 1
}

const flattenRows = (value) => {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'object') return []
  return Object.values(value).flatMap((items) => Array.isArray(items) ? items : [])
}

const extractReviews = (payload) => {
  const candidates = [
    payload?.reviews,
    payload?.list,
    payload?.business_reviews,
    payload?.data?.reviews,
    payload?.data?.list,
    payload?.data?.business_reviews,
    payload?.data,
    payload?.result
  ]

  for (const candidate of candidates) {
    const rows = flattenRows(candidate)
    if (rows.length) return rows
  }
  return []
}

const normalizeReview = (item = {}, index = 0) => {
  const review = normalizeFlyerDetail(
    { ...item, flyer_type: 'review' },
    {
      id: `business-review-${index}`,
      business_id: businessId.value,
      business_name: requestedBusinessName.value,
      flyer_type: 'review'
    }
  )
  return {
    ...review,
    id: firstValue(review.id, review.flyer_id, `business-review-${index}`),
    flyer_type: 'review',
    business_id: firstValue(review.business_id, businessId.value),
    business_name: firstValue(review.business_name, requestedBusinessName.value, 'Business')
  }
}

const loadReviews = async () => {
  if (!businessId.value) {
    reviews.value = []
    loadError.value = 'A business was not selected.'
    return
  }

  isLoading.value = true
  loadError.value = ''
  try {
    const userId = session.userId || session.user?.user_id || session.user?.userid || session.user?.id || ''
    const { data } = await api.listBusinessReviews({ business_id: businessId.value, user_id: userId })
    reviews.value = extractReviews(data).map(normalizeReview)
  } catch (error) {
    reviews.value = []
    loadError.value = error?.response?.data?.message || error?.message || 'Business reviews could not be loaded.'
  } finally {
    isLoading.value = false
  }
}

watch(businessId, loadReviews)

onMounted(() => {
  updateColumnCount()
  window.addEventListener('resize', updateColumnCount)
  loadReviews()
})

onUnmounted(() => window.removeEventListener('resize', updateColumnCount))
</script>

<style scoped>
.business-reviews-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.business-reviews-column {
  display: grid;
  min-width: 0;
  gap: 1rem;
  align-content: start;
}

.business-reviews-column :deep(.flyer-card),
.business-reviews-column :deep(.flyer-card-skeleton) {
  margin-bottom: 0;
}

@media (min-width: 768px) {
  .business-reviews-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .business-reviews-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
